<script setup>
import { ref, computed, watch, defineProps, defineEmits } from "vue";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import { useTerritoriosStore } from "@/store/territorios";
import { useStorage } from "@/composables/useStorage";
import { useApi } from "@/composables/useApi";
import { useNominatim } from "@/composables/useNominatim";
import { useScreenStore } from "@/store/useScreenStore";
import MapaBase from "@/components/MapaBase/MapaBase.vue";
import { useAppToast } from "@/composables/useAppToast";

const toast = useAppToast();
const screen = useScreenStore();
const territorios = useTerritoriosStore();
const { saveConfig } = useStorage();
const api = useApi();
const { reverseGeocode } = useNominatim();

const props = defineProps({
  territoriosSeleccionados: { type: Array, default: () => [] },
});
const emit = defineEmits(["saved"]);

const visible = ref(false);
const saving = ref(false);

const emptyForm = () => ({
  name: "",
  direccion: "",
  latlng: { lat: 0, lng: 0 },
});

const form = ref(emptyForm());
const asociacion = ref(null);

const opcionesAsociacion = computed(() => [
  { value: null, label: "Sin territorio (global)" },
  ...props.territoriosSeleccionados.map((key) => ({
    value: key,
    label: territorios.byKey[key]?.territorio_nombre || key,
  })),
]);

const click = async (e) => {
  form.value.latlng = e.latlng;
  // Autocompletar la dirección por reverse geocoding
  try {
    const address = await reverseGeocode(e.latlng);
    const a = address?.address;
    form.value.direccion =
      (a?.house_number ? `${a.road} ${a.house_number}`.trim() : null) ??
      address?.intersection ??
      a?.road ??
      address?.name ??
      address?.display_name?.split(",")[0] ??
      "";
  } catch (err) {
    console.error("Error en reverse geocoding:", err);
  }
};

// Referencias al mapa Leaflet (provistas por MapaBase vía @ready)
let leafletMap = null;
let leaflet = null;
const terrLayers = new Map(); // key -> polígono dibujado

const clearTerrLayers = () => {
  if (leafletMap) {
    terrLayers.forEach((layer) => leafletMap.removeLayer(layer));
  }
  terrLayers.clear();
};

// Dibuja todos los territorios asignados a la salida y encuadra para verlos
const drawTerritorios = () => {
  clearTerrLayers();
  if (!leafletMap || !leaflet) return;

  props.territoriosSeleccionados.forEach((key) => {
    const t = territorios.byKey[key];
    if (!t || !Array.isArray(t.limits) || t.limits.length <= 1) return;
    const bg = t.color?.backgroundColor || "#6b7280";
    const poly = leaflet
      .polygon(t.limits, {
        color: bg,
        weight: 2,
        opacity: 0.9,
        fillColor: bg,
        fillOpacity: 0.25,
        interactive: false,
      })
      .addTo(leafletMap);
    terrLayers.set(key, poly);
  });

  if (terrLayers.size) {
    const group = leaflet.featureGroup([...terrLayers.values()]);
    leafletMap.fitBounds(group.getBounds(), { padding: [20, 20] });
  }
};

// Centra el mapa en el territorio que se está asignando
const centerTerritorio = () => {
  if (!leafletMap || !leaflet || asociacion.value === null) return;
  const layer = terrLayers.get(asociacion.value);
  if (layer) {
    leafletMap.fitBounds(layer.getBounds(), { padding: [20, 20] });
    return;
  }
  const t = territorios.byKey[asociacion.value];
  if (t?.center) leafletMap.flyTo(t.center, t.zoom || 16);
};

const onMapReady = (e) => {
  leafletMap = e.map();
  leaflet = e.m().L;
  drawTerritorios();
  centerTerritorio();
};

// El mapa se destruye al cerrar el diálogo: descartamos las referencias
const onHide = () => {
  leafletMap = null;
  leaflet = null;
  terrLayers.clear();
};

watch(asociacion, () => centerTerritorio());
watch(
  () => props.territoriosSeleccionados,
  () => drawTerritorios(),
  { deep: true }
);

const open = () => {
  form.value = emptyForm();
  asociacion.value = null;
  visible.value = true;
};

const save = async () => {
  const direccion = form.value.direccion?.trim();
  if (!direccion) {
    toast.warn("Falta la dirección", "Ingrese la dirección del punto de encuentro.");
    return;
  }
  if (!form.value.latlng?.lat && !form.value.latlng?.lng) {
    toast.warn("Falta la ubicación", "Seleccione una ubicación en el mapa.");
    return;
  }

  saving.value = true;
  try {
    const punto = {
      id: crypto.randomUUID(),
      // El nombre es opcional; si no se ingresa, se usa la dirección
      name: form.value.name?.trim() || direccion,
      direccion,
      latlng: JSON.parse(JSON.stringify(form.value.latlng)),
    };

    if (asociacion.value === null) {
      // Punto global (sin territorio)
      territorios.$patch({
        puntoEncuentro: [...territorios.puntoEncuentro, punto],
      });
      await saveConfig(['puntoEncuentro']);
    } else {
      // Asociado a un territorio seleccionado
      const updated = territorios.addPuntoEncuentroToTerritorio(
        asociacion.value,
        punto
      );
      if (!updated) {
        toast.error("No se encontró el territorio seleccionado.");
        return;
      }
      await api.put(`/api/territories/${updated.uuid}`, updated);
    }

    emit("saved", punto);
    visible.value = false;
    form.value = emptyForm();
    asociacion.value = null;
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div>
    <Button
      label="Agregar punto de encuentro"
      icon="pi pi-plus"
      text
      @click="open"
    />
    <Dialog
      v-model:visible="visible"
      modal
      header="Nuevo punto de encuentro"
      :style="{ width: screen.isMobile ? '100%' : '50%' }"
      @hide="onHide"
    >
      <div class="flex flex-column gap-2">
        <div>
          <label class="pb-1">Dirección</label>
          <InputText
            v-model="form.direccion"
            placeholder="Ej: Av. Corrientes 1234"
            autofocus
            class="w-full"
          />
          <small class="text-500">Se completa automáticamente al marcar en el mapa.</small>
        </div>
        <div>
          <label class="pb-1">Nombre <span class="text-xs text-500">(opcional)</span></label>
          <InputText
            v-model="form.name"
            placeholder="Ej: Salón del Reino"
            class="w-full"
          />
        </div>
        <div>
          <label class="pb-1">Asociar a</label>
          <Dropdown
            v-model="asociacion"
            :options="opcionesAsociacion"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>
        <div class="text-xs text-500">
          Coordenadas: {{ form.latlng.lat }}, {{ form.latlng.lng }}
        </div>
      </div>
      <div class="pt-3">
        <MapaBase
          @mapClick="click"
          @ready="onMapReady"
          class="map"
          :setPoint="true"
          height="35vh"
        />
      </div>
      <template #footer>
        <Button
          label="Cancelar"
          icon="pi pi-times"
          @click="visible = false"
          text
        />
        <Button
          label="Guardar"
          icon="pi pi-check"
          :loading="saving"
          @click="save()"
        />
      </template>
    </Dialog>
  </div>
</template>
